/**
 * @file baseQueryWithReauth.ts
 * @description Shared baseQuery with automatic silent 401 token refresh.
 */
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type { RootState } from '../../app/store.js';
import { setCredentials, logout } from '../../features/auth/model/authSlice.js';
import { readAccessToken } from './authedFetch.js';

const rawBaseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken ?? readAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

export const createBaseQueryWithReauth = (
  baseUrl: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const baseQuery = rawBaseQuery(baseUrl);

  return async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      // Access token expired or missing — attempt silent refresh via HTTP-only cookie
      const refreshResult = await rawBaseQuery('/api/v1/auth')(
        { url: '/refresh', method: 'POST' },
        api,
        extraOptions,
      );

      if (refreshResult.data) {
        const data = refreshResult.data as any;
        if (data.success && data.data?.accessToken) {
          // Store new credentials
          api.dispatch(setCredentials({ user: data.data.user, accessToken: data.data.accessToken }));
          // Retry the original failed query with the refreshed access token
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logout());
        }
      } else {
        api.dispatch(logout());
      }
    }

    return result;
  };
};
